import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { Stocking } from '../interfaces';
import { Analysis } from '../../Analysis/interfaces';
import { getLanguage } from '../../../utils/language';
import { downloadFile } from '../../../utils/download';
import { axiosClient as axios } from '../../../utils/axios_instance';
import { analysisStatuses, transferStatuses } from '../../../config/commons';
import { TransferMetricEntry } from '../../Reports/TransferGraph/interfaces';
import { ANALYSES_URL, STOCKINGS_URL, TRANSFER_PARAMETER_URL, TRANSFER_PDF_URL, TRANSFER_SUMMARY_PDF_URL, TRANSFERS_URL, TRANSPORT_URL, TRAYS_URL } from '../../../config/config.api';

import { Parameter, ParameterType, Transport, Tray, WeighingTransfer, WeighingTransferPayload, WeighingTransferState } from './interfaces';

const initialState: WeighingTransferState = {
  isLoading: false,
  transfers: [],
  trays: [],
  parameters: [],
  transports: [],
  isDownloadingTransferPdf: false,
  isDownloadingTransferSummaryPdf: false,
  showStockingDestinationModal: false,
  isLoadingStockingDestination: false,
  capsules: {
    transferParameters: {},
    stocking: {
      _id: '',
      name: '',
      code: '',
      campusName: '',
      moduleName: '',
      tankName: '',
      startDateJuvenile: '',
    },
    transfer: {
      _id: '',
      name: '',
      transferDate: '',
      maxSmallAnimalsWeight: 0,
      histogramWeight: {
        limits: [],
        frequencies: [],
      },
      companyId: '',
      transports: [],
      userId: '',
      animalsToTransfer: 0,
      stockingId: '',
      maxSmallAnimalsPercentage: 0,
    },
  },
  showChartModal: false,
  showHistogramModal: false,
  selectedTransferMetricEntry: [],
};

export const weighingTransfersSlice = createSlice({
  name: 'weighingTransfers',
  initialState,
  reducers: {
    setIsLoading: (state: WeighingTransferState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setTransfers: (state: WeighingTransferState, action: PayloadAction<WeighingTransfer[]>) => {
      state.transfers = action.payload;
    },
    setSelectedStocking: (state: WeighingTransferState, action: PayloadAction<Stocking>) => {
      state.selectedStocking = action.payload;
    },
    setStockingDestination: (state: WeighingTransferState, action: PayloadAction<Stocking | undefined>) => {
      state.stockingDestination = action.payload;
    },
    setTrays: (state: WeighingTransferState, action: PayloadAction<Tray[]>) => {
      state.trays = action.payload;
    },
    setParameters: (state: WeighingTransferState, action: PayloadAction<Parameter[]>) => {
      state.parameters = action.payload;
    },
    setTransports: (state: WeighingTransferState, action: PayloadAction<Transport[]>) => {
      state.transports = action.payload;
    },
    setIsDownloadingTransferPdf: (state: WeighingTransferState, action: PayloadAction<boolean>) => {
      state.isDownloadingTransferPdf = action.payload;
    },
    setIsDownloadingTransferSummaryPdf: (state: WeighingTransferState, action: PayloadAction<boolean>) => {
      state.isDownloadingTransferSummaryPdf = action.payload;
    },
    setShowStockingDestinationModal: (state: WeighingTransferState, action: PayloadAction<boolean>) => {
      state.showStockingDestinationModal = action.payload;
    },
    setIsLoadingStockingDestination: (state: WeighingTransferState, action: PayloadAction<boolean>) => {
      state.isLoadingStockingDestination = action.payload;
    },
    setCapsules: (state: WeighingTransferState, action: PayloadAction<WeighingTransferPayload>) => {
      state.capsules.transferParameters = action.payload.transferParameters;
      state.capsules.stocking = action.payload.stocking;
      state.capsules.transfer = action.payload.transfer;
    },
    setShowChartModal: (state: WeighingTransferState, action: PayloadAction<boolean>) => {
      state.showChartModal = action.payload;
    },
    setShowHistogramModal: (state: WeighingTransferState, action: PayloadAction<boolean>) => {
      state.showHistogramModal = action.payload;
    },
    setSelectedTransferMetricEntry: (state: WeighingTransferState, action: PayloadAction<TransferMetricEntry[]>) => {
      state.selectedTransferMetricEntry = action.payload;
    },
    setSelectedParameter: (state: WeighingTransferState, action: PayloadAction<ParameterType | undefined>) => {
      state.selectedParameter = action.payload;
    },
    
  },
});

export const {
  setIsLoading,
  setTransfers,
  setSelectedStocking,
  setStockingDestination,
  setTrays,
  setParameters,
  setTransports,
  setIsDownloadingTransferPdf,
  setIsDownloadingTransferSummaryPdf,
  setShowStockingDestinationModal,
  setIsLoadingStockingDestination,
  setCapsules,
  setShowChartModal,
  setShowHistogramModal,
  setSelectedTransferMetricEntry,
  setSelectedParameter,
} = weighingTransfersSlice.actions;

export const fetchStocking = (props: {stockingId: string; }) => async (dispatch: Function) => {
  const { stockingId } = props;
  dispatch(setIsLoading(true));

  const url = `${STOCKINGS_URL}/${stockingId}`;
  const params = {
    $select: ['name', 'phaseType', 'companyId'],
  };

  try {
    const response = await axios.get<Stocking>(url, { params });
    dispatch(setSelectedStocking(response.data));
  } catch (error) {
    console.log(error?.response);
  }
  dispatch(setIsLoading(false));
};

export const fetchStockingDestination = (props: {campusId: string; moduleId: string; tankId: string; code: string}) => async (dispatch: Function) => {
  const params = {
    ...props,
    $limit: -1,
    $select: ['campusName', 'moduleName', 'tankName', 'name', 'code', 'status'],
  };

  dispatch(setIsLoadingStockingDestination(true));
  dispatch(setStockingDestination(initialState.stockingDestination));

  try {
    const response = await axios.get<Stocking[]>(STOCKINGS_URL, { params });
    const stockings = response.data;

    if (stockings && stockings.length) {
      dispatch(setStockingDestination(stockings[0]));
    }
  } catch (error) {
    console.log(error?.response);
  }

  dispatch(setIsLoadingStockingDestination(false));
};

export const fetchWeighingTransfers = (props: {stockingId: string; companyId: string;}) => async (dispatch: Function) => {
  const { stockingId, companyId } = props;
  dispatch(setIsLoading(true));

  const params = {
    stockingId,
    companyId,
    active: true,
    $limit: -1,
    '$sort[transferDate]': -1,
    '$sort[_id]': -1,
    'status[$in][0]': transferStatuses.ACTIVE,
    'status[$in][1]': transferStatuses.COMPLETED,
    $select: ['transferDate', 'name', 'status']
  };

  try {
    const response = await axios.get<WeighingTransfer[]>(TRANSFERS_URL, { params });
    dispatch(setTransfers(response.data));
  } catch (e) {
    console.log(e?.response);
  }
  dispatch(setIsLoading(false));
};

export const fetchTrays = (props: { companyId: string; transferId: string; }) => async (dispatch: Function) => {
  const { companyId, transferId } = props;
  const params = {
    companyId,
    transferId,
    $limit: -1,
    active: true,
  };

  try {
    const response = await axios.get<Tray[]>(TRAYS_URL, { params });
    dispatch(setTrays(response.data));
  } catch (error) {
    console.log(error?.response);
  }
};

export const fetchParameters = (props: { companyId: string; transferId: string; }) => async (dispatch: Function) => {
  const params = {
    ...props,
    $limit: -1,
    active: true,
  };

  try {
    const response = await axios.get<Parameter[]>(TRANSFER_PARAMETER_URL, { params });
    dispatch(setParameters(response.data));
  } catch (error) {
    console.log(error?.response);
  }
};

export const fetchTransports = (companyId: string) => async (dispatch: Function) => {
  const params = {
    companyId,
    $limit: -1,
    active: true,
  };

  dispatch(setTransports([]));

  try {
    const response = await axios.get<Transport[]>(TRANSPORT_URL, { params });
    dispatch(setTransports(response.data));
  } catch (error) {
    console.log(error?.response);
  }
};

export const fetchAnalysisByTray = async (props: {trayId: string; companyId: string; stockingId: string; transferId: string;}) => {
  const { trayId, companyId, stockingId, transferId } = props;

  const params = {
    $select: ['_id'],
    trayId,
    sowingId: stockingId,
    companyId,
    transferId,
    $limit: -1,
    status: analysisStatuses.COMPLETED,
  };
  
  try {
    const response = await axios.get<Analysis[]>(ANALYSES_URL, { params: params });
    if (response.data && response.data.length) {
      const analysisId = response.data[0]._id;
      const analysisUrl = `/production/analysis/${analysisId}`;
      window.open(analysisUrl, '_blank');
    }
  } catch (error) {
    console.log(error?.response);
  }
};

export const downloadTransferSummaryPdf = (params: { stockingId: string; stockingName: string; companyId: string; }) => async (dispatch: Function) => {
  const { stockingId, stockingName, companyId } = params;
  dispatch(setIsDownloadingTransferSummaryPdf(true));

  try {
    const response = await axios.get<{url: string}>(TRANSFER_SUMMARY_PDF_URL);
    const language = getLanguage();
    const accessToken = localStorage.getItem('accessToken');
    const url = `${response.data.url}?stockingId=${stockingId}&companyId=${companyId}&accessToken=${accessToken}&language=${language}`;
    await downloadFile(url, stockingName, 'pdf');
  } catch (error) {
    console.log(error?.response);
  }
  dispatch(setIsDownloadingTransferSummaryPdf(false));
};

export const downloadTransferPdf = (params: { transferId: string; transferName: string; companyId: string; }) => async (dispatch: Function) => {
  const { transferId, transferName, companyId } = params;
  dispatch(setIsDownloadingTransferPdf(true));

  try {
    const response = await axios.get<{url: string}>(TRANSFER_PDF_URL);
    const language = getLanguage();
    const accessToken = localStorage.getItem('accessToken');
    const url = `${response.data.url}?transferId=${transferId}&companyId=${companyId}&accessToken=${accessToken}&language=${language}`;
    await downloadFile(url, transferName, 'pdf');
  } catch (error) {
    console.log(error?.response);
  }
  dispatch(setIsDownloadingTransferPdf(false));
};

export const fetchTransferChartData = (props: { transferId: string; }) => async (dispatch: Function) => {
  const { transferId } = props;

  const url = `${TRANSFERS_URL}/${transferId}/chart-data`;

  try {
    const response = await axios.get<WeighingTransferPayload>(url);
    dispatch(setCapsules(response.data));
  } catch (error) {
    console.log(error?.response);
  }
};

export default weighingTransfersSlice.reducer;
